import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet, useLocation } from 'react-router-dom';

import { cn } from '@/lib/utils';
import ROUTES from '@/shared/constants/routes';
import { useInvoiceV2Tracking, useTrackView } from '../hooks/useInvoiceV2Tracking';
import { QrMaintenanceGate } from '../qr/components/QrMaintenanceGate';
import { InvoiceTools } from './components/InvoiceTools';
import { QuotaPaywall } from './components/QuotaCard';
import { WizardBar, type WizardStep } from './components/WizardBar';
import { useInvoiceFlow } from './hooks/useInvoiceFlow';
import { useInvoiceQuota } from './hooks/useInvoiceQuota';
import type { WizardOutletContext } from './hooks/useStampStageChrome';

const STEP_BY_PATH: Record<string, WizardStep> = {
  [ROUTES.FACTURACION_CONCEPTS]: 'conceptos',
  [ROUTES.FACTURACION_PAYMENT]: 'pago',
  [ROUTES.FACTURACION_REVIEW]: 'revisar',
};

const scrollParentOf = (element: HTMLElement | null): HTMLElement | null => {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};

/**
 * El scroll lo lleva el `<main>` del layout (o el panel acoplado del
 * asistente), no la ventana, así que nadie lo regresa arriba al cambiar de
 * ruta: el CTA de avanzar vive al final del paso y el siguiente aparecía ya
 * desplazado. Cada paso arranca arriba y entra con un fade corto.
 *
 * Con la escena del timbrado montada (`useStampStageChrome`) la barra de pasos
 * cede su lugar a la del módulo, y el fondo es el blanco de la pantalla de éxito.
 *
 * Con el cupo agotado, el paso cede su lugar al aviso (`QuotaPaywall`). Nunca a
 * mitad de la escena del timbrado: ahí el desenlace, éxito o 429, ya lo cuenta ella.
 */
const WizardShell = () => {
  const location = useLocation();
  const { draft, reset } = useInvoiceFlow();
  const { trackStepViewed } = useInvoiceV2Tracking();
  const step = STEP_BY_PATH[location.pathname] ?? 'conceptos';
  const [stampStage, setStampStage] = useState(false);
  const outletContext = useMemo<WizardOutletContext>(() => ({ setStampStage }), []);
  const shellRef = useRef<HTMLDivElement>(null);
  const { quota, exhausted } = useInvoiceQuota();
  const paywall = exhausted && !stampStage ? quota : undefined;

  useLayoutEffect(() => {
    scrollParentOf(shellRef.current)?.scrollTo({ top: 0 });
  }, [location.pathname, stampStage]);

  useTrackView(draft.receiver && !paywall ? `${draft.id}:${step}` : null, () =>
    trackStepViewed(draft.id, step),
  );

  return (
    <div ref={shellRef} className={cn('min-h-full', stampStage ? 'bg-white' : 'bg-h-bg-app')}>
      {stampStage ? (
        <InvoiceTools showEmissionMode={false} />
      ) : (
        <WizardBar current={step} onExit={reset} />
      )}
      {paywall ? (
        <QuotaPaywall quota={paywall} />
      ) : (
        <div
          key={location.pathname}
          className='animate-in fade-in-0 slide-in-from-bottom-1 duration-200 ease-out motion-reduce:animate-none'>
          <Outlet context={outletContext} />
        </div>
      )}
    </div>
  );
};

export const invoicingV2Routes: RouteObject[] = [
  // Subir el CSD tiene valor por sí solo: se llega desde los avisos del home
  // y del wizard, no desde el sidebar.
  {
    id: 'invoicing-v2-stamps',
    path: ROUTES.FACTURACION_STAMPS,
    handle: { pageWidth: 'full' },
    lazy: async () => ({
      Component: (await import('./views/StampsView')).StampsView,
    }),
  },
  // The invoicing plans by quota: reached from the wizard paywall, the plan sheet
  // of the home or the stamping error, not from the sidebar.
  {
    id: 'invoicing-v2-plans',
    path: ROUTES.FACTURACION_PLANES,
    handle: { pageWidth: 'full' },
    lazy: async () => ({
      Component: (await import('./views/InvoicePlansView')).InvoicePlansView,
    }),
  },
  // The PDF brand, entered from the home. Without the flag, or without pdf_brand (the
  // plan or the backend's allowlist), the view goes back home.
  {
    id: 'invoicing-v2-pdf-brand',
    path: ROUTES.FACTURACION_MARCA,
    handle: { pageWidth: 'full' },
    lazy: async () => ({
      Component: (await import('./views/PdfBrandView')).PdfBrandView,
    }),
  },
  // La página de facturación por QR: sección propia en el sidebar, debajo de
  // «Emitir factura».
  {
    id: 'invoicing-v2-qr-owner',
    element: (
      <QrMaintenanceGate surface='owner'>
        <Outlet />
      </QrMaintenanceGate>
    ),
    children: [
      {
        id: 'invoicing-v2-qr',
        path: ROUTES.FACTURACION_QR,
        handle: { pageWidth: 'full' },
        lazy: async () => ({
          Component: (await import('./views/QrConfigView')).QrConfigView,
        }),
      },
      {
        id: 'invoicing-v2-qr-requests',
        path: ROUTES.FACTURACION_QR_REQUESTS,
        handle: { pageWidth: 'full' },
        lazy: async () => ({
          Component: (await import('./views/QrRequestsView')).QrRequestsView,
        }),
      },
      {
        id: 'invoicing-v2-qr-request',
        path: ROUTES.FACTURACION_QR_REQUEST,
        handle: { pageWidth: 'full' },
        lazy: async () => ({
          Component: (await import('./views/QrRequestView')).QrRequestView,
        }),
      },
    ],
  },
  // El provider del borrador lo monta `InvoicingLayout`, arriba de estas
  // rutas y del home.
  {
    id: 'invoicing-v2',
    element: <Outlet />,
    handle: { pageWidth: 'full' },
    children: [
      {
        id: 'invoicing-v2-new-client',
        path: ROUTES.FACTURACION_NEW_CLIENT,
        lazy: async () => ({
          Component: (await import('./views/NewClientView')).NewClientView,
        }),
      },
      {
        id: 'invoicing-v2-stamped',
        path: ROUTES.FACTURACION_STAMPED,
        lazy: async () => ({
          Component: (await import('./views/StampedInvoiceView')).StampedInvoiceView,
        }),
      },
      {
        id: 'invoicing-v2-my-invoice',
        path: ROUTES.FACTURACION_MY_INVOICE,
        lazy: async () => ({
          Component: (await import('./views/MyInvoiceView')).MyInvoiceView,
        }),
      },
      {
        id: 'invoicing-v2-wizard',
        element: <WizardShell />,
        children: [
          {
            id: 'invoicing-v2-concepts',
            path: ROUTES.FACTURACION_CONCEPTS,
            lazy: async () => ({
              Component: (await import('./views/ConceptsView')).ConceptsView,
            }),
          },
          {
            id: 'invoicing-v2-payment',
            path: ROUTES.FACTURACION_PAYMENT,
            lazy: async () => ({
              Component: (await import('./views/PaymentView')).PaymentView,
            }),
          },
          {
            id: 'invoicing-v2-review',
            path: ROUTES.FACTURACION_REVIEW,
            lazy: async () => ({
              Component: (await import('./views/ReviewRoute')).ReviewRoute,
            }),
          },
        ],
      },
    ],
  },
  // Registrar el pago de una factura PPD: el complemento de pago (tipo P).
  {
    id: 'invoicing-v2-payment-complement',
    handle: { pageWidth: 'full' },
    lazy: async () => ({
      Component: (await import('./views/PaymentComplementLayout')).PaymentComplementLayout,
    }),
    children: [
      {
        id: 'invoicing-v2-payment-complement-invoice',
        path: ROUTES.FACTURACION_COMPLEMENTO,
        lazy: async () => ({
          Component: (await import('./views/PaymentInvoiceView')).PaymentInvoiceView,
        }),
      },
      {
        id: 'invoicing-v2-payment-complement-data',
        path: ROUTES.FACTURACION_COMPLEMENTO_PAGO,
        lazy: async () => ({
          Component: (await import('./views/PaymentAmountView')).PaymentAmountView,
        }),
      },
      {
        id: 'invoicing-v2-payment-complement-review',
        path: ROUTES.FACTURACION_COMPLEMENTO_REVISAR,
        lazy: async () => ({
          Component: (await import('./views/PaymentReviewView')).PaymentReviewView,
        }),
      },
      {
        id: 'invoicing-v2-payment-complement-done',
        path: ROUTES.FACTURACION_COMPLEMENTO_LISTO,
        lazy: async () => ({
          Component: (await import('./views/PaymentStampedView')).PaymentStampedView,
        }),
      },
    ],
  },
];
